import { useMutation, UseMutationReturnType, useQuery, useQueryClient, UseQueryReturnType } from '@tanstack/vue-query';
import useImpersonation from '@/hooks/useImpersonation';
import { CurrentUserService } from 'hiber-grpc';
import { getGrpcClient } from '@/services/grpc-client';
import {
  CurrentUser,
  UpdateMissionControlSettingsRequest,
  UpdateMissionControlSettingsRequest_Response,
  UpdateDefaultOrganizationRequest_Response,
  UpdateDefaultOrganizationRequest,
} from 'hiber-grpc/src/customer_compiled/currentuser';
import { ClientError } from 'nice-grpc-web';
import useServiceErrorHandler from '@/hooks/useServiceErrorHandler';
import useCurrentUser from '@/hooks/useCurrentUser';
import { computed, MaybeRef, unref } from 'vue';
import { minutesToMilliseconds } from 'date-fns';
import { useUserStore } from '@/store/user';

export function useCurrentUserQuery(options?: {
  enabled?: MaybeRef<boolean>;
}): UseQueryReturnType<CurrentUser, unknown> {
  const userStore = useUserStore();
  const org = useImpersonation();
  const currentUser = useCurrentUser();
  const enabled = computed(() => unref(options?.enabled) ?? true);

  return useQuery({
    queryKey: ['currentUser/get', org, currentUser],
    queryFn: () => CurrentUserService.getInstance(getGrpcClient).getCurrentUser(),
    enabled,
    refetchOnWindowFocus: 'always',
    refetchInterval: minutesToMilliseconds(15),
    refetchIntervalInBackground: true,
    refetchOnReconnect: 'always',
    onSuccess: (data: CurrentUser) => {
      userStore.setCurrentUser(data);
    },
  });
}

export function useUpdateSettingsMutation(): UseMutationReturnType<
  UpdateMissionControlSettingsRequest_Response,
  ClientError,
  UpdateMissionControlSettingsRequest,
  unknown
> {
  const queryClient = useQueryClient();
  const onError = useServiceErrorHandler();

  return useMutation({
    mutationFn: (payload: UpdateMissionControlSettingsRequest) =>
      CurrentUserService.getInstance(getGrpcClient).updateSettings(payload),
    onSuccess: () => {
      (async () => {
        await queryClient.invalidateQueries({
          exact: false,
          predicate: (query) => query.queryHash.indexOf('currentUser') > -1,
        });
      })();
    },
    onError: (error: ClientError) => {
      onError(error);
    },
  });
}

export function useUpdateDefaultOrganizationMutation(): UseMutationReturnType<
  UpdateDefaultOrganizationRequest_Response,
  ClientError,
  UpdateDefaultOrganizationRequest,
  unknown
> {
  const onError = useServiceErrorHandler();

  return useMutation({
    mutationFn: (payload: UpdateDefaultOrganizationRequest) =>
      CurrentUserService.getInstance(getGrpcClient).updateDefaultOrganization(payload),
    onError: (error: ClientError) => {
      onError(error);
    },
  });
}
